import { useEffect, useState } from 'react';
import { Button, Stack } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { InstitutionView, InstitutionViewRow } from 'cb-model';
import { fetchApi, GCacheRecord, notify, selectUser, useCacheRecord } from 'gossamer-front-end';
import { canLeaveInstitution, CustomInfo } from 'cb-unified';

type FollowInstitutionProps = {
  institutionRecord: GCacheRecord<InstitutionViewRow>;
};

export default function FollowInstitution(props: FollowInstitutionProps) {
  const user = useSelector(selectUser);
  const customInfo = user?.customInfo as CustomInfo | undefined;
  const followingInstitutionIds = (customInfo?.followingInstitutionIds || []) as number[];
  const institutionId = props.institutionRecord.data?.id;
  const isOriginallyFollower = followingInstitutionIds.includes(Number(institutionId));
  const isAdmin = Number(customInfo?.adminInstitutionId) === Number(institutionId);

  const [isFollowing, setIsFollowing] = useState<boolean>(isOriginallyFollower);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsFollowing(isAdmin || isOriginallyFollower);
  }, [isOriginallyFollower, isAdmin]);

  const institutionRecord = useCacheRecord(InstitutionView, String(institutionId), !institutionId);
  const followerCountFromView = institutionRecord.data?.follower_count ?? 0;
  const [followerCount, setFollowerCount] = useState<number>(followerCountFromView);

  useEffect(() => {
    setFollowerCount(followerCountFromView);
  }, [followerCountFromView]);

  const formattedFollowers = followerCount.toLocaleString();
  const canUnfollow = canLeaveInstitution(
    customInfo,
    Number(institutionId),
    institutionRecord.data?.type,
  );

  const disableFollow = isAdmin || !user || !institutionId || isLoading || (isFollowing && !canUnfollow);

  async function toggleFollow() {
    if (!user) return;
    if (isAdmin || (isFollowing && !canUnfollow)) return;

    setIsLoading(true);
    try {
      const response = await fetchApi('POST', `/main/institution-member-${isFollowing ? 'leave' : 'join'}`, {
        institutionId,
      });
      if (response.error) {
        notify([response.error, 'danger']);
        console.error(response.error);
      } else {
        // setCurrentInstitutionId(institutionId);
        setIsFollowing(!isFollowing);
        setFollowerCount((c) => c + (isFollowing ? -1 : 1));
      }
    } catch (error) {
      notify([error.message || 'An error occurred while trying to follow/unfollow the institution', 'danger']);
      console.error('Error toggling follow institution', error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Stack direction="horizontal" gap={2}>
      <div
        style={{
          border: '1px solid var(--bs-gray-500)',
          padding: '0.25rem 0.5rem',
          borderRadius: '0.25rem',
          fontSize: '0.875rem',
        }}
      >
        {formattedFollowers} follower{followerCount !== 1 ? 's' : ''}
      </div>
      <Button disabled={disableFollow} size="sm" onClick={toggleFollow}>
        {isFollowing ? 'Following' : 'Follow'}
      </Button>
    </Stack>
  );
}
